<script setup lang="ts">
  import { watch } from "vue";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Switch } from "@/components/ui/switch";
  import { DateTimePicker } from "@/components/episto-ui/date-time-picker";
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
  import { FormDialog } from "@/components/episto-ui/form-dialog";
  import AnnouncementCard from "@/components/Announcements/AnnouncementCard.vue";

  export interface AnnouncementForm {
    id?: number;
    title: string;
    body: string;
    url: string;
    starts_on: string;
    ends_on: string | null;
    active: boolean;
  }

  const announcement = defineModel<AnnouncementForm>("announcement", { required: true });

  const props = defineProps<{
    open: boolean;
    isEditing?: boolean;
  }>();

  const emit = defineEmits<{
    "update:open": [value: boolean];
    save: [];
  }>();

  const toPickerValue = (date: string | null | undefined) => (date ? `${date}T00:00:00` : null);

  const toDateOnly = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const validationSchema = toTypedSchema(
    z
      .object({
        title: z
          .string()
          .max(80, "80 characters maximum")
          .refine((title) => title.trim().length > 0, "Please enter a title"),
        body: z.string().max(240, "240 characters maximum"),
        url: z.string().regex(/^https:\/\/\S+$/, "Please enter a valid https URL"),
        starts_on: z.string().min(1, "Please pick a start date"),
        ends_on: z.string().nullable(),
        active: z.boolean(),
      })
      .superRefine((data, ctx) => {
        if (data.ends_on && data.ends_on < data.starts_on) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "The end date must be on or after the start date",
            path: ["ends_on"],
          });
        }
      }),
  );

  const { handleSubmit, values, resetForm } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: { ...announcement.value },
  });

  watch(values, (v) => Object.assign(announcement.value, v));

  watch(
    () => props.open,
    (open) => {
      if (open) resetForm({ values: { ...announcement.value } });
    },
  );

  const handleSave = handleSubmit(() => emit("save"));
</script>

<template>
  <FormDialog
    :open="open"
    :is-editing="isEditing"
    entity-name="Announcement"
    max-width="600px"
    @update:open="emit('update:open', $event)"
    @save="handleSave">
    <FormField
      v-slot="{ componentField }"
      name="title">
      <FormItem>
        <FormLabel>Title</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="e.g. Analysis grids are live"
            class="text-sm" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="body">
      <FormItem>
        <FormLabel>Text</FormLabel>
        <FormControl>
          <Textarea
            v-bind="componentField"
            rows="3"
            placeholder="One or two sentences on what changes for the user"
            class="text-sm" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="url">
      <FormItem>
        <FormLabel>Documentation link</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="https://episto.slite.page/p/..."
            class="text-sm font-mono" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div class="grid grid-cols-2 gap-4">
      <FormField
        v-slot="{ value, handleChange }"
        name="starts_on">
        <FormItem>
          <FormLabel>Start date</FormLabel>
          <FormControl>
            <DateTimePicker
              :model-value="toPickerValue(value)"
              :clearable="false"
              placeholder="Pick a start date"
              @update:model-value="handleChange(toDateOnly($event))" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ value, handleChange }"
        name="ends_on">
        <FormItem>
          <FormLabel>End date</FormLabel>
          <FormControl>
            <DateTimePicker
              :model-value="toPickerValue(value)"
              placeholder="No end date"
              @update:model-value="handleChange(toDateOnly($event))" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <FormField
      v-slot="{ value, handleChange }"
      name="active">
      <FormItem>
        <FormLabel>Active</FormLabel>
        <FormControl>
          <Switch
            id="announcement-active"
            :model-value="value"
            @update:model-value="handleChange" />
        </FormControl>
      </FormItem>
    </FormField>

    <div class="space-y-2">
      <p class="text-sm font-medium">Preview</p>
      <div class="w-[240px] rounded-lg bg-sidebar p-2">
        <AnnouncementCard
          :announcement="{
            title: values.title || 'Title',
            body: values.body ?? null,
            url: values.url || '#',
          }" />
      </div>
    </div>
  </FormDialog>
</template>
